import React, { useEffect, useRef } from "react";

// Animated dot-grid shader that sits behind the workspace. Verbatim port of the
// WebGL2 fragment/vertex shaders from docs/cowork-dot-shader.html — only the
// React/lifecycle wiring and the theme-driven dot color are new.

const VERT = `#version 300 es
in vec2 a_position;
out vec2 v_uv;
uniform vec2 u_resolution;
void main() {
  v_uv = (a_position * 0.5 + 0.5) * u_resolution;
  gl_Position = vec4(a_position, 0.0, 1.0);
}`;

const FRAG = `#version 300 es
precision highp float;
uniform vec2 u_resolution;
uniform float u_time;
uniform vec4 u_color;
in vec2 v_uv;
out vec4 fragColor;

const float GAP   = 22.0;
const float DOT_R = 1.6;
const float R_MIN = 1.0;

const float ARMS  = 2.0;
const float PITCH = 0.08;
const float SPIN  = 0.25;
const float DRIFT = 0.035;

const float WIDTH = 0.55;
const float GAMMA = 2.2;

const float RIPPLE_AMP  = 3.0;
const float RIPPLE_FREQ = 0.020;
const float RIPPLE_VEL  = 0.12;
const float RIPPLE_TURN = 0.015;

void main() {
  vec2 cell = floor(v_uv / GAP);

  float h = u_time * RIPPLE_TURN;
  vec2 k1 = vec2(cos(h), sin(h));
  vec2 k2 = vec2(cos(h + 1.05), sin(h + 1.05));
  float s1 = sin(dot(cell, k1) * RIPPLE_FREQ * 6.2831853 - u_time * RIPPLE_VEL);
  float s2 = sin(dot(cell, k2) * RIPPLE_FREQ * 6.2831853 - u_time * RIPPLE_VEL * 0.8);
  vec2 disp = (k1 * s1 + k2 * s2) * RIPPLE_AMP * 0.5;

  vec2 local = v_uv - (cell + 0.5) * GAP - disp;
  float dist = length(local);

  vec2 gridSize = u_resolution / GAP;
  vec2 origin = gridSize * 0.5 + gridSize * 0.35 * vec2(
    sin(u_time * DRIFT * 3.0),
    cos(u_time * DRIFT * 2.0)
  );

  vec2 d = cell - origin;
  float rad = length(d);
  float ang = atan(d.y, d.x);

  float phase = ang * ARMS + rad * PITCH - u_time * SPIN;
  float dArm = acos(cos(phase)) / 3.14159265;

  float t = max(1.0 - dArm / WIDTH, 0.0);

  float a = pow(t, GAMMA);
  float r = mix(R_MIN, DOT_R, t);

  float aa = fwidth(dist);
  float mask = 1.0 - smoothstep(r - aa, r + aa, dist);

  fragColor = u_color * (a * mask);
}`;

// Read "r, g, b" (0-255) from --ca-dot; fall back to white. Alpha is baked in.
const DOT_ALPHA = 0.5;
function readDotColor(host) {
  const raw = getComputedStyle(host).getPropertyValue("--ca-dot").trim();
  const parts = raw.split(",").map((n) => parseFloat(n) / 255);
  const [r, g, b] = parts.length === 3 && parts.every((n) => !Number.isNaN(n))
    ? parts
    : [1, 1, 1];
  // premultiplied alpha: (r*a, g*a, b*a, a)
  return [r * DOT_ALPHA, g * DOT_ALPHA, b * DOT_ALPHA, DOT_ALPHA];
}

export default function DotGridBackground({ className = "" }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const gl = canvas.getContext("webgl2", {
      alpha: true, antialias: false, depth: false, stencil: false, premultipliedAlpha: true,
    });
    if (!gl) return undefined; // ponytail: no WebGL2 → no background, no crash

    const compile = (type, src) => {
      const s = gl.createShader(type);
      gl.shaderSource(s, src); gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) console.warn(gl.getShaderInfoLog(s));
      return s;
    };
    const prog = gl.createProgram();
    gl.attachShader(prog, compile(gl.VERTEX_SHADER, VERT));
    gl.attachShader(prog, compile(gl.FRAGMENT_SHADER, FRAG));
    gl.linkProgram(prog); gl.useProgram(prog);

    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(prog, "a_position");
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

    const uTime = gl.getUniformLocation(prog, "u_time");
    const uRes = gl.getUniformLocation(prog, "u_resolution");
    const uCol = gl.getUniformLocation(prog, "u_color");

    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA); // premultiplied
    gl.clearColor(0, 0, 0, 0);

    const applyColor = () => gl.uniform4f(uCol, ...readDotColor(canvas));
    applyColor();
    // Re-read the dot color when the app theme flips (data-theme on <html>).
    const themeObserver = new MutationObserver(applyColor);
    themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const w = Math.round(canvas.clientWidth * dpr);
      const hgt = Math.round(canvas.clientHeight * dpr);
      if (canvas.width !== w || canvas.height !== hgt) {
        canvas.width = w; canvas.height = hgt;
        gl.viewport(0, 0, w, hgt);
        gl.uniform2f(uRes, w, hgt);
      }
    };

    const start = performance.now();
    let raf = 0;
    const frame = (now) => {
      resize();
      gl.uniform1f(uTime, (now - start) * 0.000575); // 50% slower, then +15%
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(raf);
      themeObserver.disconnect();
      gl.getExtension("WEBGL_lose_context")?.loseContext();
    };
  }, []);

  return <canvas ref={canvasRef} className={`ca-bg-canvas ${className}`.trim()} aria-hidden="true" />;
}
